/**
 * useDismissable — el comportamiento común de todo lo que se abre encima.
 *
 * Portado de `clobi-design-system@0.1.0`. Lo comparten `Modal` y `Drawer`, y
 * resuelve las cuatro cosas que el `Modal` del repo no hacía:
 *
 *   1. **Escape cierra.**
 *   2. **El Tab cicla dentro** (trampa de foco). Sin esto se puede tabular hasta
 *      el contenido de atrás sin verlo: el lector de pantalla se va del diálogo
 *      y quien navega con teclado queda perdido.
 *   3. **El fondo no scrollea** mientras está abierto.
 *   4. **El foco vuelve** al elemento que lo abrió al cerrar, en vez de saltar
 *      al principio de la página.
 *
 * ⚠️ Las cuatro se limpian en el mismo `useEffect`: si el desmontaje no
 * restaura el `overflow` del body, la página queda sin scroll y parece colgada.
 *
 * 🔴 **`onClose` NO va en las dependencias del efecto, y esa es la parte que
 * hay que cuidar.** El cleanup devuelve el foco a quien abrió el diálogo, así
 * que si el efecto se remonta en cada render el foco se escapa del modal
 * apenas el usuario toca una tecla. Y se remonta en cada render con cualquier
 * `onClose={() => …}` inline, que es lo que hacen los 168 usos del repo: la
 * función es una identidad nueva cada vez. Medido antes de arreglarlo —
 * escribir «abc» en un campo dentro del modal dejaba el valor en «a» y el foco
 * afuera. Por eso el callback vive en un ref y las dependencias son solo
 * `[open, modal]`: el efecto monta una vez por apertura.
 */
import { useEffect, useRef } from 'react'

/**
 * Lo que puede recibir foco. `[tabindex="-1"]` queda fuera a propósito: es
 * enfocable por código pero no por Tab, así que incluirlo rompería el ciclo.
 */
const ENFOCABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), ' +
  'input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

export interface DismissableOptions {
  open: boolean
  onClose?: () => void
  /**
   * `true` atrapa el foco y bloquea el scroll del fondo. `false` deja la
   * superficie de atrás usable, que es el caso de un panel lateral no modal.
   */
  modal?: boolean
}

export function useDismissable({ open, onClose, modal = true }: DismissableOptions) {
  const contenedor = useRef<HTMLElement | null>(null)
  const disparador = useRef<Element | null>(null)

  // El callback se lee en el momento del evento, nunca al montar, así que
  // sacarlo de las dependencias no deja ninguna versión vieja en uso.
  const alCerrar = useRef(onClose)
  useEffect(() => {
    alCerrar.current = onClose
  })

  useEffect(() => {
    if (!open) return undefined

    disparador.current = document.activeElement

    const alPresionar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        alCerrar.current?.()
        return
      }
      if (!modal || e.key !== 'Tab' || !contenedor.current) return

      // `offsetParent === null` descarta lo que está oculto: tabular hacia algo
      // invisible deja el foco en ninguna parte visible.
      const enfocables = Array.from(
        contenedor.current.querySelectorAll<HTMLElement>(ENFOCABLE),
      ).filter(el => el.offsetParent !== null)
      if (enfocables.length === 0) return

      const primero = enfocables[0]
      const ultimo = enfocables[enfocables.length - 1]
      if (e.shiftKey && document.activeElement === primero) {
        e.preventDefault()
        ultimo.focus()
      } else if (!e.shiftKey && document.activeElement === ultimo) {
        e.preventDefault()
        primero.focus()
      }
    }

    document.addEventListener('keydown', alPresionar)

    let overflowPrevio: string | undefined
    if (modal) {
      overflowPrevio = document.body.style.overflow
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', alPresionar)
      if (modal) document.body.style.overflow = overflowPrevio ?? ''
      const previo = disparador.current as HTMLElement | null
      if (previo?.focus) previo.focus()
    }
  }, [open, modal])

  return contenedor
}
